import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert, Box, Button, Card, CardContent, CardHeader, Checkbox, Chip, Collapse, Divider,
  Grid, IconButton, Link, ListItemText, Menu, MenuItem, Stack, Table, TableBody, TableCell, TableContainer,
  TableFooter, TableHead, TablePagination, TableRow, TextField,
  Tooltip, Typography,
} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import ClearIcon from '@mui/icons-material/Clear'
import { LoadingButton } from '@mui/lab'
import { PieChart, Pie, Cell, Tooltip as RTooltip, Legend, ResponsiveContainer } from 'recharts'
import { useSnackbar } from 'notistack'
import Loader from '../../common/Loader'
import { apiConstants, post } from '../../common/constants'
import MerchantSelect from './MerchantSelect'
import { downloadAllocationsCsv, requiredServiceType } from './allocationExport'
import { useGetAllActiveMerchantsIdAndNameCachedQuery } from '../../redux/commonApiCalls'
import {
  PACE_PRESETS, fetchMerchantConfig, fetchShadowRecords, fetchShadowSummary, presetLabel, rescoreAllocation,
} from './decisionConfigService'

/**
 * PACE Allocations — the PACE allocation records (live + shadow) with per-order ranking
 * explainer, filters and stats. PACE live + shadow-compare are edited on the Merchant
 * List page; here the selected merchant's current state is shown read-only.
 */
export default function EngineTab() {
  const { enqueueSnackbar } = useSnackbar()
  const [merchant, setMerchant] = useState(null)      // full object from MerchantSelect
  const [engine, setEngine] = useState(null)          // read-only display of merchant's engine
  const [shadow, setShadow] = useState(false)         // read-only display of shadow-compare state
  const [loading, setLoading] = useState(false)

  const [days, setDays] = useState(7)
  const [summary, setSummary] = useState(null)
  const [records, setRecords] = useState([])
  const [dataLoading, setDataLoading] = useState(false)
  const [openId, setOpenId] = useState(null)          // expanded row (ranking explainer)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [total, setTotal] = useState(0)
  const [modeFilter, setModeFilter] = useState('')        // '' = all, 'LIVE', 'SHADOW'
  const [referenceFilter, setReferenceFilter] = useState('')  // committed reference query
  const [referenceInput, setReferenceInput] = useState('')    // text box value (commit on Enter/blur)

  // Rows accumulated across every page the user has loaded for the current query, keyed by id.
  // Lets selection persist across pages and lets Download run entirely client-side (no API call).
  // Reset whenever the query (merchant / window / mode / page size) changes.
  const [rowsById, setRowsById] = useState({})
  const [selectedIds, setSelectedIds] = useState(() => new Set())

  const merchantOid = merchant?.id || ''
  // Rules pick / Agree / Δcost / Δtime need the live-engine pick, which LIVE rows don't have — drop those 4 columns
  // when the grid is filtered to LIVE so the table shows only what's meaningful.
  const showV1Cols = modeFilter !== 'LIVE'
  const COLS = showV1Cols ? 12 : 8

  // Readable merchant name for the grid column, keyed by numeric merchantId — same cached
  // list + nomenclature as the merchant dropdown / Forward Shipments page.
  const { data: merchantList = [] } = useGetAllActiveMerchantsIdAndNameCachedQuery()
  const merchantById = useMemo(() => {
    const map = {}
    merchantList.forEach((m) => { if (m.merchantId != null) map[String(m.merchantId)] = m })
    return map
  }, [merchantList])
  const merchantLabel = (r) => {
    const m = merchantById[String(r.merchantId)]
    if (m) return m.companyName ? `${m.name} (${m.companyName})` : m.name
    return r.merchantOid || '—'
  }

  // Options object (not positional): the filter set keeps growing, so callers override only what
  // changed and everything else defaults from current state. Called right after setState, so the
  // changed value must be passed explicitly (state hasn't flushed yet).
  const loadRecords = (opts = {}) => {
    const {
      d = days, m = merchant, p = page, sz = rowsPerPage,
      md = modeFilter, ref = referenceFilter, reset = false,
    } = opts
    setDataLoading(true)
    setOpenId(null)
    if (reset) { setRowsById({}); setSelectedIds(new Set()) }
    fetchShadowRecords(d, p, sz, m?.merchantId, md || undefined, ref || undefined)
      .then((r) => {
        // Tolerate both shapes: new paginated { content, total } and the legacy bare array
        // (so the grid works whether or not the backend pagination is deployed yet).
        const body = r.data
        const rows = Array.isArray(body) ? body : (body?.content || [])
        setRecords(rows)
        setTotal(Array.isArray(body) ? body.length : (body?.total ?? rows.length))
        // Accumulate into the client-side store so selection + download span every loaded page.
        setRowsById((prev) => {
          const base = reset ? {} : { ...prev }
          rows.forEach((row) => { base[String(row.id)] = row })
          return base
        })
      })
      .catch(() => enqueueSnackbar('Failed to load allocation records', { variant: 'error' }))
      .finally(() => setDataLoading(false))
  }

  const loadComparison = (d = days, m = merchant, md = modeFilter) => {
    setPage(0)
    // Summary shape follows the mode: LIVE ⇒ PACE-only (count + avg cost/ETA), else agreement view.
    fetchShadowSummary(d, m?.merchantId, md || undefined)
      .then((s) => setSummary(s.data))
      .catch(() => enqueueSnackbar('Failed to load allocation summary', { variant: 'error' }))
    loadRecords({ d, m, p: 0, md, reset: true })
  }
  useEffect(() => { loadComparison(7) }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Paging keeps the accumulated store (reset=false) so selection/download span all loaded pages.
  const onPageChange = (_e, p) => { setPage(p); loadRecords({ p, reset: false }) }
  const onRowsPerPageChange = (e) => {
    const sz = parseInt(e.target.value, 10)
    setRowsPerPage(sz); setPage(0); loadRecords({ sz, p: 0, reset: true })
  }
  // Mode drives both the grid and the stats shape, so re-pull the summary too.
  const onModeFilter = (e) => {
    const md = e.target.value
    setModeFilter(md); setPage(0)
    fetchShadowSummary(days, merchant?.merchantId, md || undefined)
      .then((s) => setSummary(s.data))
      .catch(() => enqueueSnackbar('Failed to load allocation summary', { variant: 'error' }))
    loadRecords({ md, p: 0, reset: true })
  }
  // Reference is committed on Enter/blur (not per keystroke) to avoid a request per character.
  const commitReference = () => {
    const ref = referenceInput.trim()
    if (ref === referenceFilter) return
    setReferenceFilter(ref); setPage(0); loadRecords({ ref, p: 0, reset: true })
  }

  const load = (oid = merchantOid) => {
    if (!oid) return
    setLoading(true)
    fetchMerchantConfig(oid)
      .then((res) => {
        setEngine((res.data?.allocationEngineVersion || 'V1').toUpperCase())
        setShadow(Boolean(res.data?.paceV2ShadowEnabled))
      })
      .catch(() => { setEngine(null); enqueueSnackbar('Failed to load merchant config', { variant: 'error' }) })
      .finally(() => setLoading(false))
  }

  const selectMerchant = (m) => {
    setMerchant(m)
    setEngine(null)
    if (m?.id) load(m.id)
    loadComparison(days, m)
  }

  const num = (v, d = 0) => (v == null ? '—' : Number(v).toFixed(d))

  // ── Reference → order detail ─────────────────────────────────────────────
  // order-detail/:id resolves by AWB (+ ?id=<orderId> to disambiguate). PACE ranks before the
  // order is registered, so a row only carries the AWB once PACE's nightly ledger has matched
  // the shipment. Without it, a click resolves reference + merchant → {awb, id} with ONE order
  // search (cached per row; no call on page load). The tab is opened synchronously and pointed at the page once the
  // lookup returns, so popup blockers don't eat it.
  const orderLinkCache = useRef({})
  const openOrderDetail = async (r) => {
    const key = `${r.merchantOid || ''}|${r.reference}`
    const tab = window.open('', '_blank')
    try {
      let hit = orderLinkCache.current[key] || (r.awb ? { awb: r.awb } : null)
      if (!hit) {
        const andfilter = [{ field: 'reference', operator: 'eq', value: r.reference }]
        if (r.merchantOid) andfilter.push({ field: 'merchantDetail.key', operator: 'eq', value: r.merchantOid })
        const res = await post(apiConstants.ORDER_SEARCH, { andfilter, includes: 'awbNumber,reference' })
        const orders = res?.data?.data || []
        // newest first when a reference was re-booked; the AWB-bearing one is the shipment
        const o = orders.find((x) => x.awb_number || x.awbNumber) || orders[0]
        hit = o ? { awb: o.awb_number || o.awbNumber, id: o.id } : null
        if (hit?.awb) orderLinkCache.current[key] = hit
      }
      if (!hit?.awb) {
        if (tab) tab.close()
        enqueueSnackbar(hit ? `Order ${r.reference} has no AWB yet — nothing to open`
          : `No order found for reference ${r.reference}`, { variant: 'info' })
        return
      }
      const url = `/order-detail/${encodeURIComponent(hit.awb)}${hit.id ? `?id=${encodeURIComponent(hit.id)}` : ''}`
      if (tab) tab.location.href = url
      else window.open(url, '_blank', 'noopener')
    } catch (e) {
      if (tab) tab.close()
      enqueueSnackbar(`Could not open order ${r.reference}`, { variant: 'error' })
    }
  }

  // ── Selection (spans all loaded pages; ids normalised to strings) ─────────────
  const rid = (r) => String(r.id)
  const pageIds = records.map(rid)
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id))
  const somePageSelected = pageIds.some((id) => selectedIds.has(id))
  const loadedCount = Object.keys(rowsById).length
  const selectedCount = selectedIds.size

  const toggleRow = (id) => setSelectedIds((prev) => {
    const next = new Set(prev)
    if (next.has(id)) next.delete(id); else next.add(id)
    return next
  })
  const togglePage = () => setSelectedIds((prev) => {
    const next = new Set(prev)
    if (allPageSelected) pageIds.forEach((id) => next.delete(id))
    else pageIds.forEach((id) => next.add(id))
    return next
  })
  const selectAllLoaded = () => setSelectedIds(new Set(Object.keys(rowsById)))
  const clearSelection = () => setSelectedIds(new Set())

  // ── Client-side CSV download (no backend call — built from rows already in the UI) ──
  // Carries everything the grid + the per-order "Why this ranking" explainer show: one row
  // per (allocation × courier considered). Column contract lives in allocationExport.js.
  const fileName = (tag) => {
    const who = merchant?.name ? merchant.name.replace(/[^\w.-]+/g, '_').slice(0, 40) : 'all-merchants'
    const mode = modeFilter ? `_${modeFilter.toLowerCase()}` : ''
    const stamp = new Date().toISOString().slice(0, 10)
    return `pace_v2_allocations_${who}_${days}d${mode}_${tag}_${stamp}.csv`
  }
  const downloadRows = (rowObjs, tag) => {
    const clean = rowObjs.filter(Boolean)
    if (clean.length === 0) { enqueueSnackbar('No rows to download', { variant: 'info' }); return }
    const n = downloadAllocationsCsv(fileName(tag), clean, merchantLabel)
    enqueueSnackbar(`Downloaded ${clean.length} allocation(s) · ${n} CSV row(s)`, { variant: 'success' })
  }
  const downloadSelected = () => downloadRows(Object.values(rowsById).filter((r) => selectedIds.has(rid(r))), 'selected')
  const downloadAllLoaded = () => downloadRows(Object.values(rowsById), 'all-loaded')

  return (
    <Card>
      {loading && <Loader />}
      <CardHeader title="Allocations (per merchant)"
        subheader="PACE allocation records — live + shadow. PACE live + shadow-compare are edited on the Merchant List page." />
      <CardContent>
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={12} md={5}>
            <MerchantSelect value={merchant} onChange={selectMerchant} />
          </Grid>
          <Grid item xs={12} md={7}>
            <LoadingButton variant="outlined" loading={loading} disabled={!merchantOid} onClick={() => load()}>
              Reload
            </LoadingButton>
          </Grid>
        </Grid>

        {engine && (
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: 'wrap', mb: 1 }}>
            <Typography variant="body2">Current state:</Typography>
            <Chip size="small" color={engine === 'V2' ? 'success' : 'default'}
              label={engine === 'V2' ? 'PACE live' : 'Allocation rules decide'} />
            <Chip size="small" variant="outlined" label={`Shadow-compare: ${shadow ? 'on' : 'off'}`} />
            <Typography variant="caption" color="text.secondary">
              (read-only here — change it on the Merchant List page)
            </Typography>
          </Stack>
        )}

        <Alert severity="info" sx={{ mt: 2 }}>
          PACE ranks the couriers of rules that carry the PACE entry; if PACE errors, the rule's own
          courier order is used. Shadow rows are PACE run alongside the rules' decision (comparison
          only); live rows are PACE actually deciding the allocation.
        </Alert>

        <Divider sx={{ my: 3 }} />

        {/* ── rules vs PACE comparison ─────────────────────────────────── */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}>
          <Typography variant="subtitle2">
            Allocations ({merchant ? merchant.name : 'all merchants'})
          </Typography>
          <TextField select size="small" label="Window" value={days}
            onChange={(e) => { setDays(e.target.value); loadComparison(e.target.value) }}
            SelectProps={{ native: true }} sx={{ width: 120 }}>
            <option value={1}>1 day</option>
            <option value={7}>7 days</option>
            <option value={30}>30 days</option>
          </TextField>
          <TextField select size="small" label="Mode" value={modeFilter}
            onChange={onModeFilter}
            SelectProps={{ native: true }} sx={{ width: 130 }}>
            <option value="">All modes</option>
            <option value="LIVE">Live</option>
            <option value="SHADOW">Shadow</option>
          </TextField>
          <TextField size="small" label="AWB / Reference" value={referenceInput}
            onChange={(e) => setReferenceInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') commitReference() }}
            onBlur={commitReference}
            placeholder="exact AWB or ref · any date"
            sx={{ width: 220 }}
            InputProps={referenceFilter ? {
              endAdornment: (
                <IconButton size="small" onClick={() => { setReferenceInput(''); setReferenceFilter(''); setPage(0); loadRecords({ ref: '', p: 0, reset: true }) }}>
                  <ClearIcon fontSize="inherit" />
                </IconButton>
              ),
            } : undefined} />
          <LoadingButton size="small" loading={dataLoading} onClick={() => loadComparison()}>Refresh</LoadingButton>
        </Box>

        {/* LIVE has no rules pick to compare against → a PACE-only count. Shadow/All keeps the rules-vs-PACE
            agreement summary. */}
        {summary?.mode === 'LIVE' ? (
          <Stack direction="row" spacing={4} sx={{ flexWrap: 'wrap', mb: 2 }}>
            <Metric label="Live allocations" value={summary.total ?? 0} />
          </Stack>
        ) : (
          summary && (
            <Stack direction="row" spacing={4} sx={{ flexWrap: 'wrap', mb: 2 }}>
              <Metric label="Shadow-compared" value={summary.total ?? 0} />
              <Metric label="Rules = PACE (top-1)" value={summary.agreeTop1 ?? 0} />
              <Metric label="Agreement"
                value={summary.agreementRatePct != null ? `${summary.agreementRatePct}%` : '—'} />
            </Stack>
          )
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
          <Typography variant="caption" color="text.secondary">
            {selectedCount} selected · {loadedCount} loaded{total ? ` of ${total} total` : ''}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Button size="small" onClick={selectAllLoaded} disabled={loadedCount === 0}>Select all loaded</Button>
          <Button size="small" onClick={clearSelection} disabled={selectedCount === 0}>Clear</Button>
          <Tooltip title={CSV_HELP}>
            <span>
              <Button size="small" variant="outlined" startIcon={<FileDownloadOutlinedIcon />}
                onClick={downloadSelected} disabled={selectedCount === 0}>
                Download selected{selectedCount ? ` (${selectedCount})` : ''}
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={CSV_HELP}>
            <span>
              <Button size="small" variant="contained" startIcon={<FileDownloadOutlinedIcon />}
                onClick={downloadAllLoaded} disabled={loadedCount === 0}>
                Download all loaded{loadedCount ? ` (${loadedCount})` : ''}
              </Button>
            </span>
          </Tooltip>
        </Box>

        <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox size="small" checked={allPageSelected}
                  indeterminate={!allPageSelected && somePageSelected}
                  onChange={togglePage} disabled={pageIds.length === 0} />
              </TableCell>
              <TableCell padding="checkbox" />
              <TableCell>Merchant</TableCell>
              <TableCell>
                <Tooltip title="AWB on top, the order reference below. The AWB appears once PACE's nightly ledger has matched the shipment (next day); until then only the reference shows.">
                  <span>AWB / Reference</span>
                </Tooltip>
              </TableCell>
              <TableCell>Mode</TableCell>
              {showV1Cols && (
                <TableCell>
                  <Tooltip title="What the rules picked: the first courier in the matched rule's own priority list. (Rows from before Nov 2026 with the PACE entry show the retired rule ranker's #1.)">
                    <span>Rules pick</span>
                  </Tooltip>
                </TableCell>
              )}
              <TableCell>PACE pick</TableCell>
              {showV1Cols && <TableCell align="center">Agree</TableCell>}
              <TableCell align="right">PACE cost</TableCell><TableCell align="right">PACE ETA (h)</TableCell>
              {showV1Cols && <><TableCell align="right">Δ cost (rules−PACE)</TableCell><TableCell align="right">Δ time (rules−PACE)</TableCell></>}
            </TableRow>
          </TableHead>
          <TableBody>
            {records.map((r) => {
              const open = openId === r.id
              return (
                <Fragment key={r.id}>
                  <TableRow hover sx={{ cursor: 'pointer', '& > *': { borderBottom: open ? 'unset' : undefined }, ...(r.error ? { bgcolor: 'rgba(255,193,7,0.12)' } : {}) }}
                    onClick={() => setOpenId(open ? null : r.id)}>
                    <TableCell padding="checkbox">
                      <Checkbox size="small" checked={selectedIds.has(rid(r))}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => toggleRow(rid(r))} />
                    </TableCell>
                    <TableCell padding="checkbox">
                      <IconButton size="small">{open ? <KeyboardArrowUpIcon fontSize="inherit" /> : <KeyboardArrowDownIcon fontSize="inherit" />}</IconButton>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={`${r.merchantOid || ''}${r.merchantId ? ` · id ${r.merchantId}` : ''}`}>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>{merchantLabel(r)}</Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Link component="button" underline="hover" title="Open order detail in a new tab"
                        onClick={(e) => { e.stopPropagation(); openOrderDetail(r) }}
                        sx={{ color: '#3783e7', fontWeight: 700, fontSize: 12, fontFamily: 'monospace',
                              textAlign: 'left', cursor: 'pointer' }}>
                        {r.awb || r.reference}
                      </Link>
                      {r.awb && (
                        <Typography variant="caption" color="text.secondary" component="div"
                          sx={{ fontFamily: 'monospace' }}>
                          {r.reference}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        <Chip size="small" variant="outlined" label={r.mode} />
                        <ServiceAskedChip required={requiredServiceType(r)} />
                      </Stack>
                    </TableCell>
                    {showV1Cols && <TableCell><Typography variant="caption">{r.v1TopAccountCode || '—'}</Typography></TableCell>}
                    <TableCell><Typography variant="caption">{r.v2TopAccountCode || (r.error ? 'error' : '—')}</Typography></TableCell>
                    {showV1Cols && (
                      <TableCell align="center">
                        {r.mode !== 'SHADOW' ? '—'
                          : <Chip size="small" label={r.agreeTop1 ? '✓' : '✗'} color={r.agreeTop1 ? 'success' : 'default'} />}
                      </TableCell>
                    )}
                    <TableCell align="right">{num(r.v2EstTotalCost)}</TableCell>
                    <TableCell align="right">{num(r.v2EstTransitHours)}</TableCell>
                    {showV1Cols && <><TableCell align="right"><DeltaCell value={r.deltaCost} /></TableCell>
                    <TableCell align="right"><DeltaCell value={r.deltaTransitHours} /></TableCell></>}
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={COLS} sx={{ py: 0, borderBottom: open ? undefined : 'none' }}>
                      <Collapse in={open} timeout="auto" unmountOnExit>
                        <RankedExplainer row={r} />
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </Fragment>
              )
            })}
            {records.length === 0 && (
              <TableRow><TableCell colSpan={COLS}>
                <Typography variant="caption" color="text.secondary">
                  No PACE allocation records yet for this filter (turn PACE live or shadow-compare on for a merchant and let orders flow).
                </Typography>
              </TableCell></TableRow>
            )}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination
                colSpan={COLS}
                count={total}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={onPageChange}
                onRowsPerPageChange={onRowsPerPageChange}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </TableRow>
          </TableFooter>
        </Table>
        </TableContainer>
      </CardContent>
    </Card>
  )
}

const CSV_HELP = 'CSV of the rows already loaded (no extra fetch) — grid columns plus every courier in '
  + 'the ranking explainer: score, Delivery/FASR/FATA/Logistics value·weight·contribution·share, '
  + 'ETA, cost split (rate-card + WD + risk), chargeable weight. One row per courier considered; '
  + 'filter first_row_of_allocation = 1 for one row per allocation, is_pace_pick = 1 for the PACE pick.'

function Metric({ label, value }) {
  return (
    <Box>
      <Typography variant="h6">{value}</Typography>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
    </Box>
  )
}

// Per-row signed delta (rules − PACE). Positive/green = PACE cheaper or faster.
function DeltaCell({ value }) {
  if (value == null) return <Typography variant="caption" color="text.secondary">—</Typography>
  const better = value >= 0
  return (
    <Typography variant="caption" sx={{ color: better ? 'success.main' : 'error.main' }}>
      {value > 0 ? `+${value}` : value}
    </Typography>
  )
}

// Older stored rows (before unified scoring) ranked on the α sliding scale — α and the
// knobs come from the merchant/global Business Knobs.
const ALPHA_FORMULA =
  'score = α·λ_fast·(−ETA) + (1−α)·λ_cheap·(−cost), where '
  + 'cost = rate-card quote + WD surcharge + risk_weight × (RTO + reattempt + lost). '
  + 'α and the knobs come from the merchant/global Business Knobs (risk_weight default 0 = risk off).'
// Older stored rows ranked on the empirical weighted scorecard.
const SCORECARD_FORMULA =
  'score = Σ wᵢ·factorᵢ over Delivery% / FASR% / FATA (First-Attempt TAT Adherence) (Wilson LB, recency-weighted) and cost, each normalised across the eligible couriers. Merchant-tuned weights.'
// Unified scoring (docs/07): empirical quality weights + a soft-capped α speed/cost bucket.
const UNIFIED_FORMULA =
  'score = w_del·Delivery + w_fasr·FASR + w_fata·FATA + w_logi·L, where '
  + 'FATA = First-Attempt TAT Adherence (first attempt within EDD), and '
  + 'L = cap/(cap + penalty) with penalty = α·λ_fast·ETA + (1−α)·λ_cheap·cost. '
  + 'Quality factors are empirical Wilson % (ML-head fallback for new couriers); the logistics '
  + 'bucket blends speed & cost by α on the λ scale, soft-capped so it can’t swamp quality but '
  + 'still ranks slow/expensive couriers (penalty = cap ⇒ L = 0.5; never a flat 0). '
  + 'w_logi is the "Weight: Logistics (cost + speed)" knob (= w_cost).'

// Score-contribution palette (also used by the pie). Delivery green, FASR blue, FATA orange,
// Logistics purple — consistent across the chart, legend and factor table.
const FACTOR_COLORS = { Delivery: '#2e7d32', FASR: '#1976d2', FATA: '#ed6c02', Logistics: '#8e24aa' }

// Expanded explainer for one order: the full PACE ranked list with the score decomposition,
// so ops can see *why* this is the order. Built from v2_ranked_json.
function RankedExplainer({ row }) {
  const { enqueueSnackbar } = useSnackbar()
  const [rescored, setRescored] = useState(null)   // { ranked, knobs } or null
  const [rescoring, setRescoring] = useState(false)
  const [presetAnchor, setPresetAnchor] = useState(null)   // "Re-score as ▾" menu anchor

  let stored = []
  try { stored = JSON.parse(row.v2RankedJson || '[]') } catch (e) { stored = [] }
  if (row.error) {
    return <Box sx={{ p: 2 }}><Typography variant="caption" color="error">PACE call failed: {row.error}</Typography></Box>
  }
  if (!stored.length) {
    return <Box sx={{ p: 2 }}><Typography variant="caption" color="text.secondary">No ranked data stored for this order.</Typography></Box>
  }

  const ranked = rescored ? rescored.ranked : stored
  const required = (rescored && rescored.required) || requiredServiceType(row)
  // rows ranked before PACE read courier tiers carry service_tier 'unknown' on every courier
  const tiersKnown = ranked.some((c) => c && c.service_type)
  // Unified rows carry a per-courier `unified` block (quality weights + α speed/cost).
  // Older stored rows carry either the α-scale cost_breakdown/balanced_terms or a scorecard.
  const isUnified = Array.isArray(ranked) && ranked.some((c) => c && c.unified)
  const isScorecard = !isUnified && Array.isArray(ranked) && ranked.some((c) => c && c.scorecard)
  // Rescore is a SHADOW evaluation tool. A live row is an already-executed PACE decision whose
  // shipment has shipped — re-deciding it changes nothing, and "from scratch" would re-run against
  // today's model/quotes (not the moment it shipped), so its result can't be reconciled with what
  // actually happened. Disable both rescore actions for live rows.
  const isLive = row.mode === 'LIVE'

  // Re-score this stored allocation via the proship proxy → PACE. Display-only.
  //  'config'  = keep stored ML predictions, refresh quote + apply current knobs (fast).
  //  'scratch' = full re-allocate: fresh heads + scorecard + pricing under current config.
  // `rescoring` holds the active mode string (or false) so the right button shows loading.
  //  preset    = config re-score under a named PACE preset (PACE+ #4) — what PACE would pick
  //              if this merchant only cared about delivery / FATA / cost / speed.
  const doRescore = async (mode, preset = null) => {
    setRescoring(preset ? `preset:${preset}` : mode)
    try {
      const res = await rescoreAllocation({
        merchant_id: row.merchantId,
        reference: row.reference,
        mode,
        ...(preset ? { preset } : {}),
        candidates: stored,
      })
      if (!res || !Array.isArray(res.ranked)) throw new Error('no ranked data returned')
      setRescored({ ranked: res.ranked, knobs: res.knobs || {}, mode, preset,
        required: res.required_service_type || null })
      enqueueSnackbar(preset
        ? `Re-scored as "${presetLabel(preset)}"`
        : mode === 'scratch'
          ? 'Re-scored from scratch (fresh predictions + pricing)'
          : 'Re-scored (current config + refreshed pricing)', { variant: 'success' })
    } catch (e) {
      enqueueSnackbar(`Re-score failed: ${e?.response?.data?.error || e?.message || e}`, { variant: 'error' })
    } finally {
      setRescoring(false)
    }
  }

  return (
    <Box sx={{ p: 2, bgcolor: 'action.hover' }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.25, flexWrap: 'wrap' }}>
        <Typography variant="subtitle2">
          Why this ranking · {isUnified ? 'quality + speed/cost (business knobs)' : isScorecard ? 'weighted scorecard' : 'α sliding scale'}
        </Typography>
        {rescored && <Chip size="small" color="warning" label={`re-scored · ${rescored.preset
          ? `preset: ${presetLabel(rescored.preset)}`
          : rescored.mode === 'scratch' ? 'from scratch' : 'current config'}`} />}
        {rescored?.knobs?.payment_mode_scope && (
          <Tooltip title="The merchant's knob set for this payment mode was applied (Business Knobs → Applies to).">
            <Chip size="small" variant="outlined" label={`${rescored.knobs.payment_mode_scope} knobs`} />
          </Tooltip>
        )}
        <Box sx={{ flexGrow: 1 }} />
        {rescored && <Button size="small" onClick={() => setRescored(null)}>revert to stored</Button>}
        <Tooltip title={isLive
          ? 'Rescore is available for shadow rows only — this order was allocated live by PACE and has already shipped.'
          : "Keep the stored ML predictions; refresh the rate-card quote and apply the merchant's current knobs."}>
          <span>
            <LoadingButton size="small" variant="outlined" loading={rescoring === 'config'} disabled={isLive || !!rescoring} onClick={() => doRescore('config')}>
              Re-score (config)
            </LoadingButton>
          </span>
        </Tooltip>
        <Tooltip title={isLive
          ? 'Rescore is available for shadow rows only — this order was allocated live by PACE and has already shipped.'
          : 'Full re-allocate: re-run the ML heads + scorecard on current data + fresh pricing under current config.'}>
          <span>
            <LoadingButton size="small" variant="outlined" color="secondary" loading={rescoring === 'scratch'} disabled={isLive || !!rescoring} onClick={() => doRescore('scratch')}>
              From scratch
            </LoadingButton>
          </span>
        </Tooltip>
        <Tooltip title={isLive
          ? 'Rescore is available for shadow rows only — this order was allocated live by PACE and has already shipped.'
          : 'Compare: what PACE would pick under a named preset (stored predictions, refreshed quote). Display only — never changes live allocation.'}>
          <span>
            <LoadingButton size="small" variant="outlined" color="info"
              loading={typeof rescoring === 'string' && rescoring.startsWith('preset:')}
              disabled={isLive || !!rescoring} onClick={(e) => setPresetAnchor(e.currentTarget)}>
              Re-score as ▾
            </LoadingButton>
          </span>
        </Tooltip>
        <Menu anchorEl={presetAnchor} open={!!presetAnchor} onClose={() => setPresetAnchor(null)}>
          {PACE_PRESETS.map((p) => (
            <MenuItem key={p.key} dense onClick={() => { setPresetAnchor(null); doRescore('config', p.key) }}>
              <ListItemText primary={p.label} secondary={p.description}
                secondaryTypographyProps={{ sx: { maxWidth: 340, whiteSpace: 'normal' } }} />
            </MenuItem>
          ))}
        </Menu>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ mb: rescored ? 0.5 : 1.5, display: 'block' }}>
        {isUnified ? UNIFIED_FORMULA : isScorecard ? SCORECARD_FORMULA : ALPHA_FORMULA}
        {isLive && ' · Rescore is available for shadow rows only (this order shipped live).'}
      </Typography>
      <ServiceTierNote required={required} ranked={ranked} rescored={!!rescored} />
      {rescored && (
        <Typography variant="caption" color="warning.main" sx={{ mb: 1.5, display: 'block' }}>
          {rescored.preset
            ? `Preset "${presetLabel(rescored.preset)}" applied on top of the merchant's knobs; stored predictions kept, quote refreshed`
            : rescored.mode === 'scratch'
              ? 'Full re-allocate — ML heads + scorecard recomputed on current data + fresh pricing'
              : 'Stored predictions kept; quote refreshed + current knobs applied'}
          {rescored.knobs?.alpha != null ? ` (α ${rescored.knobs.alpha}` : ''}
          {rescored.knobs?.risk_weight != null ? `, risk_weight ${rescored.knobs.risk_weight})` : (rescored.knobs?.alpha != null ? ')' : '')}
          . Not saved — for comparison only.
        </Typography>
      )}
      <Stack spacing={1}>
        {ranked.map((c, i) => (
          <CourierScoreCard key={`${c.prozo_id}-${i}`} c={c} rank={i + 1} isV2Top={i === 0}
            required={required} tiersKnown={tiersKnown} />
        ))}
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
        {rescored ? (
          <>Re-scored top pick: <b>{ranked[0]?.prozo_id || '—'}</b> (stored PACE pick was <b>{row.v2TopAccountCode || '—'}</b>). Showing the current-config ranking; press “revert to stored” for the as-allocated one.</>
        ) : (
          <>the rules chose <b>{row.v1TopAccountCode || '—'}</b> (the matched rule's priority 1) · PACE chose <b>{row.v2TopAccountCode || '—'}</b>
          {row.agreeTop1 ? ' (agree)' : ''}. Couriers below the min delivery-probability guardrail are not shown.</>
        )}
      </Typography>
    </Box>
  )
}

// Compact per-courier row: two lines by default — (1) rank + courier + chips + score,
// (2) key metrics + the inline cost split (rate-card + WD surcharge + risk = total).
// The full breakdown (unified factor table / cost waterfall / WD weight / blend) is kept
// behind a per-row expand toggle so nothing is lost.
// Exported: PACE → External Shadow Run renders the same ranked-courier explainer
// for prospect simulations, which return the identical per-courier shape.
export function CourierScoreCard({ c, rank, isV2Top, required, tiersKnown = true }) {
  const [open, setOpen] = useState(false)
  // Unavailable = proship returned no rate-card quote for this courier on this lane
  // (PACE flags it available:false + a reason, and sinks it below all priceable couriers).
  const unavailable = c.available === false
  const top = isV2Top && !unavailable
  return (
    <Box sx={{ p: 1, border: 1, borderRadius: 1, borderColor: top ? 'success.main' : 'divider',
               bgcolor: unavailable ? 'action.disabledBackground' : 'background.paper',
               opacity: unavailable ? 0.6 : 1 }}>
      {/* Click anywhere on the always-visible rows to expand/collapse the breakdown (the chevron
          is just an affordance — pointerEvents none so the click passes through to this box). */}
      <Box onClick={() => setOpen((o) => !o)} sx={{ cursor: 'pointer' }}>
        {/* line 1 */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
          <Chip size="small" label={unavailable ? '—' : `#${rank}`} color={top ? 'success' : 'default'} />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{c.prozo_id}</Typography>
          {top && <Chip size="small" color="success" variant="outlined" label="PACE pick" />}
          {tiersKnown && <TierChip c={c} required={required} />}
          <KciChip k={c.kci} />
          {unavailable && (
            <Tooltip title={c.unavailable_reason || 'No rate-card price for this lane/zone'}>
              <Chip size="small" color="default" variant="outlined" label="unavailable · no quote" />
            </Tooltip>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Typography variant="caption" color="text.secondary">score {c.score}</Typography>
          <IconButton size="small" aria-label="details" sx={{ pointerEvents: 'none' }}>
            {open ? <KeyboardArrowUpIcon fontSize="inherit" /> : <KeyboardArrowDownIcon fontSize="inherit" />}
          </IconButton>
        </Stack>
        {/* line 2 — metrics + inline cost split */}
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 0.25, flexWrap: 'wrap' }}>
          <CompactMetrics c={c} />
          <CostInline c={c} />
        </Stack>
        {unavailable && (
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.25 }}>
            {c.unavailable_reason || 'No rate-card price — courier not priced for this lane/zone (check the rate card).'}
          </Typography>
        )}
      </Box>
      {/* full breakdown on demand */}
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box sx={{ mt: 0.5 }}>
          {c.unified ? (
            <UnifiedExplain u={c.unified} cb={c.cost_breakdown} />
          ) : c.scorecard ? (
            <ScorecardExplain sc={c.scorecard} quote={c.quote} />
          ) : (
            c.cost_breakdown && <CostWaterfall cb={c.cost_breakdown} wdAdjusted={c.wd_adjusted} />
          )}
          <KciDetail k={c.kci} />
          <WdWeight c={c} />
          {c.balanced_terms && <SpeedCostSplit bt={c.balanced_terms} />}
        </Box>
      </Collapse>
    </Box>
  )
}

// ── service type asked (SDD / NDD …) ─────────────────────────────────────────
// PACE sorts by service type BEFORE score when the order asks for one (proship sets it at
// order creation from Allocation Config → Service Types). Ladder = PACE's default, fastest first.
const TIER_LADDER = ['S2H', '24H', 'S4H', 'SDD', 'NDD', 'Surface', 'Air']

function tierOrderText(required) {
  const i = TIER_LADDER.indexOf(required)
  if (i < 0) return `${required} couriers first`
  const slower = TIER_LADDER.slice(i + 1)
  const faster = TIER_LADDER.slice(0, i).reverse()
  return [`${required} couriers first`,
    slower.length ? `then slower ones (${slower.join(', then ')})` : null,
    faster.length ? `then faster ones (${faster.join(', ')}) only as a last resort` : null,
    'and couriers with no service type in the courier master last']
    .filter(Boolean).join(', ')
}

function ServiceAskedChip({ required }) {
  if (!required) return null
  return (
    <Tooltip title={`This order asked for ${required} (Allocation Config → Service Types). PACE ranks ${tierOrderText(required)}. Score only orders couriers within the same group.`}>
      <Chip size="small" color="info" label={required} />
    </Tooltip>
  )
}

function ServiceTierNote({ required, ranked, rescored }) {
  if (!required) return null
  const known = (ranked || []).some((c) => c && c.service_type)
  return (
    <Alert severity="info" sx={{ mb: 1.5, py: 0 }}>
      <b>{required} order: service type comes before score.</b> PACE ranks {tierOrderText(required)}.
      Score only decides the order within each group, so a {required} courier can rank above a
      courier with a higher score.
      {!known && (rescored
        ? ' No courier here has a service type yet, so this re-score could not apply the preference.'
        : ' This order was ranked before PACE knew each courier\'s service type, so the preference was not applied. Use Re-score (config) to see the service-type-first order.')}
    </Alert>
  )
}

// One chip per courier: how its service type compares with what the order asked for.
function TierChip({ c, required }) {
  const tier = c.service_tier
  if (!tier || tier === 'no_tier') return null
  const req = required || 'the order'
  const st = c.service_type
  const spec = {
    required: { label: `${st} ✓`, color: 'info', variant: 'filled',
      tip: `${st} matches the service asked — ranked in the first group.` },
    fallback: { label: `${st} · slower`, color: 'default', variant: 'outlined',
      tip: `${st} is slower than ${req} — ranked after every ${req} courier, whatever its score.` },
    last_resort: { label: `${st} · faster, last resort`, color: 'warning', variant: 'outlined',
      tip: `${st} is faster (and usually costlier) than ${req} — used only after the ${req} and slower couriers.` },
    unknown: { label: 'no service type', color: 'default', variant: 'outlined',
      tip: 'This courier has no service type in the courier master, so it ranks last on this order. Fix it in the courier master.' },
  }[tier]
  if (!spec) return null
  return (
    <Tooltip title={spec.tip}>
      <Chip size="small" color={spec.color} variant={spec.variant} label={spec.label} />
    </Tooltip>
  )
}

// KCI disruption (PACE decision/kci.py): a live KCI event on this courier's pickup/drop area.
// applied → the delay is already in the ETA; shadow (kci_enabled = 0) → what it WOULD add.
const kciEventText = (e) => `${e.title || e.event_id} (${(e.legs || []).join(' + ')}, +${e.delay_h}h)`

function KciChip({ k }) {
  if (!k || !(k.delay_h > 0)) return null
  const tip = `${(k.events || []).map(kciEventText).join(' · ')}. `
    + (k.applied
      ? `ETA pushed ${k.base_eta_h}h → ${Number(k.base_eta_h + k.delay_h).toFixed(1)}h.`
      : 'KCI is in shadow (kci_enabled = 0): ETA not changed.')
  return (
    <Tooltip title={tip}>
      <Chip size="small" color="warning" variant={k.applied ? 'filled' : 'outlined'}
        label={`KCI ${k.applied ? '' : 'shadow '}+${k.delay_h}h`} />
    </Tooltip>
  )
}

function KciDetail({ k }) {
  if (!k) return null
  if (!(k.delay_h > 0)) {
    return (
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.25 }}>
        KCI: {k.matched_events} event(s) cover this courier's area, but neither the pickup nor the estimated
        delivery falls in their window — no delay.
      </Typography>
    )
  }
  return (
    <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.25 }}>
      KCI disruption: {(k.events || []).map(kciEventText).join(' · ')}.{' '}
      {k.applied
        ? <>ETA pushed <b>{k.base_eta_h}h → {Number(k.base_eta_h + k.delay_h).toFixed(1)}h</b> before scoring.</>
        : <>Shadow only (kci_enabled = 0) — would push ETA {k.base_eta_h}h → {Number(k.base_eta_h + k.delay_h).toFixed(1)}h.</>}
    </Typography>
  )
}

// One dot-separated line of the key metrics. Unified rows carry the quality factors
// (delivery / FASR / TAT); legacy rows carry the ML head probabilities. Plus ETA.
function CompactMetrics({ c }) {
  const eta = c.est_transit_hours != null ? `${c.est_transit_hours}h` : '—'
  const parts = c.unified
    ? [`Del ${pct(c.unified.delivery?.value)}`, `FASR ${pct(c.unified.fasr?.value)}`,
       `FATA ${pct(c.unified.tat?.value)}`, `ETA ${eta}`]
    : [`P(del) ${pct(c.p_delivered)}`, `P(RTO) ${pct(c.p_rto)}`,
       `1st ${pct(c.p_first_attempt_ok)}`, `ETA ${eta}`]
  return (
    <Typography variant="caption" color="text.secondary">
      {parts.join('  ·  ')}
    </Typography>
  )
}

// Inline cost split: ₹total (rate-card + WD surcharge + risk). WD carries the
// declared→adjusted weight detail in a tooltip so reconciliation info isn't lost.
function CostInline({ c }) {
  const cb = c.cost_breakdown
  if (!cb) {
    return c.est_total_cost != null
      ? <Typography variant="caption" sx={{ fontWeight: 600 }}>₹{c.est_total_cost}</Typography>
      : null
  }
  const rc = cb.quote || 0
  const wd = cb.surcharge || 0
  const risk = cb.risk_cost != null ? cb.risk_cost : 0
  const total = cb.total != null ? cb.total : rc + wd + risk
  const wdTip = c.wd_adjusted
    ? `WD-adjusted weight: declared ${c.declared_weight_g ?? '—'}g → ${c.wd_adjusted_weight_g ?? '—'}g (surcharge folded into rate-card)`
    : "courier's 30-day WD proxy (surcharge_weight × avg extra × rate)"
  return (
    <Typography variant="caption" sx={{ fontWeight: 600 }}>
      ₹{Number(total).toFixed(2)}
      <Typography component="span" variant="caption" color="text.secondary" sx={{ fontWeight: 400 }}>
        {' '}(RC ₹{rc.toFixed(0)} +{' '}
        <Tooltip title={wdTip}><span style={{ textDecoration: 'underline dotted' }}>WD ₹{wd.toFixed(0)}</span></Tooltip>
        {' '}+ Risk ₹{risk.toFixed(0)})
      </Typography>
    </Typography>
  )
}

// Colourful pie of how much each factor contributed to the score. Contributions are all
// ≥ 0 (weight × value), so slice share = share of the total score. FATA = First-Attempt TAT
// Adherence (the `tat` factor). Colours match FACTOR_COLORS / the table swatches.
function FactorPie({ u }) {
  const data = [
    { name: 'Delivery', value: Number(u.delivery?.contribution) || 0 },
    { name: 'FASR', value: Number(u.fasr?.contribution) || 0 },
    { name: 'FATA', value: Number(u.tat?.contribution) || 0 },
    { name: 'Logistics', value: Number(u.logistics?.contribution) || 0 },
  ].filter((d) => d.value > 0)
  const total = data.reduce((s, d) => s + d.value, 0) || 1
  if (!data.length) return null
  return (
    <Box sx={{ width: 210, height: 190, flexShrink: 0 }}>
      <ResponsiveContainer>
        <PieChart>
          <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="45%"
               innerRadius={34} outerRadius={64} paddingAngle={2}
               label={(d) => `${((d.value / total) * 100).toFixed(0)}%`} labelLine={false}
               isAnimationActive={false}>
            {data.map((d) => <Cell key={d.name} fill={FACTOR_COLORS[d.name]} />)}
          </Pie>
          <RTooltip formatter={(v, n) => [`${Number(v).toFixed(4)} (${((v / total) * 100).toFixed(0)}%)`, n]} />
          <Legend verticalAlign="bottom" height={26} iconType="circle" iconSize={8}
                  wrapperStyle={{ fontSize: 11 }} />
        </PieChart>
      </ResponsiveContainer>
    </Box>
  )
}

// Unified-profile explainer (docs/07): a colourful pie of each factor's share of the score,
// beside the quality table (delivery / FASR / FATA, value → weight → contribution) + the
// logistics bucket (α-blended speed & cost, soft-capped goodness). FATA = First-Attempt TAT
// Adherence (first attempt within EDD).
function UnifiedExplain({ u, cb }) {
  const L = u.logistics || {}
  const rows = [
    ['Delivery %', 'Delivery', u.delivery],
    ['FASR %', 'FASR', u.fasr],
    ['FATA %', 'FATA', u.tat],
  ]
  // The logistics scoring cost = real money only (rate-card quote + WD surcharge) — it
  // deliberately excludes risk (RTO/reattempt/lost), which is priced via the quality factors
  // above, not the cost. Show it decomposed so the number is relatable to the rate card.
  const rc = cb?.quote
  const wd = cb?.surcharge
  const costTip = (rc != null)
    ? `Logistics cost = rate-card ₹${rc.toFixed(2)} + WD surcharge ₹${(wd || 0).toFixed(2)}. `
      + 'Risk (RTO/reattempt/lost) is NOT in the cost — it is priced via the quality factors below.'
    : 'Rate-card quote + WD surcharge (risk excluded — priced via the quality factors).'
  return (
    <Box sx={{ mt: 0.75 }}>
      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mb: 0.5 }}>
        <Chip size="small" variant="outlined"
          label={`quality: ${u.quality_source === 'scorecard' ? 'empirical' : 'ML fallback'}`}
          sx={{ height: 20, fontSize: 11 }} />
        {L.eta_h != null && <MetricChip label="ETA" v={`${L.eta_h}h`} />}
        {L.cost != null && (
          <Tooltip title={costTip}>
            <span>
              <MetricChip label="cost" v={rc != null ? `₹${L.cost} = RC ₹${rc.toFixed(0)} + WD ₹${(wd || 0).toFixed(0)}` : `₹${L.cost}`} />
            </span>
          </Tooltip>
        )}
        {L.alpha != null && <MetricChip label="α" v={L.alpha} />}
      </Stack>
      <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ flexWrap: 'wrap' }}>
        <FactorPie u={u} />
        <Box sx={{ flex: 1, minWidth: 260 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.25 }}>
            Contribution to score
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'auto auto auto auto', gap: '2px 12px', fontSize: 11 }}>
            <b>factor</b><b>value</b><b>weight</b><b>contribution</b>
            {rows.map(([label, key, f]) => (
              <Fragment key={label}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: FACTOR_COLORS[key], display: 'inline-block' }} />
                  {label}
                </span>
                <span>{pct(f?.value)}</span>
                <span>{f?.weight}</span>
                <span>{f?.contribution}</span>
              </Fragment>
            ))}
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: FACTOR_COLORS.Logistics, display: 'inline-block' }} />
              Logistics (α speed/cost)
            </span>
            <span>L={L.l_good}</span>
            <span>{L.weight}</span>
            <span>{L.contribution}</span>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            FATA = First-Attempt TAT Adherence (first attempt within EDD). Logistics penalty =
            α({L.alpha})·λ_fast·ETA + (1−α)·λ_cheap·cost = {L.penalty}; soft-capped → L = cap/(cap + penalty) = {L.cap}/({L.cap} + {L.penalty}) = {L.l_good}.
            {u.quality_source === 'ml_heads' && ' Quality from ML heads (no empirical scorecard yet); FATA uses first-attempt odds as a proxy.'}
          </Typography>
        </Box>
      </Stack>
    </Box>
  )
}

// Weighted-profile explainer: the empirical scorecard behind the score — the
// cascade grain + recency-weighted support, and per factor raw → Wilson LB (the
// value actually scored) → weight → contribution. Makes the small-sample
// discount and which cohort level was used visible.
function ScorecardExplain({ sc, quote }) {
  const rows = [
    ['Delivery %', sc.delivery],
    ['FASR %', sc.fasr],
    ['FATA %', sc.tat],
  ]
  return (
    <Box sx={{ mt: 0.75 }}>
      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mb: 0.5 }}>
        <Chip size="small" variant="outlined" label={`grain: ${sc.grain}`} sx={{ height: 20, fontSize: 11 }} />
        <Chip size="small" variant="outlined" label={`support: ${sc.support_n}`} sx={{ height: 20, fontSize: 11 }} />
        {quote != null && <MetricChip label="quote" v={`₹${quote}`} />}
      </Stack>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'auto auto auto auto', gap: '2px 12px', fontSize: 11 }}>
        <b>factor</b><b>raw → Wilson (n)</b><b>weight</b><b>contribution</b>
        {rows.map(([label, f]) => (
          <Fragment key={label}>
            <span>{label}</span>
            <span>{pct(f.raw)} → <b>{pct(f.wilson)}</b> (n={f.n})</span>
            <span>{f.weight}</span>
            <span>{f.contribution}</span>
          </Fragment>
        ))}
        <span>Cost</span>
        <span>₹{sc.cost.logistics}</span>
        <span>{sc.cost.weight}</span>
        <span>{sc.cost.contribution}</span>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
        Rate factors use the Wilson 95% lower bound; grain = finest cohort level with ≥ min support.
        Recency ×: 7d {sc.recency_mult?.['7d']} · 30d {sc.recency_mult?.['30d']} · 90d {sc.recency_mult?.['90d']}.
      </Typography>
    </Box>
  )
}

function MetricChip({ label, v }) {
  return <Chip size="small" variant="outlined" label={`${label}: ${v}`} sx={{ height: 20, fontSize: 11 }} />
}

const pct = (v) => (v == null ? '—' : `${(v * 100).toFixed(0)}%`)

// Two cost buckets (task #6). Real cost = honest money you actually pay; the
// rate-card line is broken out first so ops can tally it against the courier
// rate card. Risk cost = priced RTO/reattempt/loss, gated by risk_weight.
const REAL_PARTS = [
  { key: 'quote', label: 'rate-card', color: '#1976d2' },
  { key: 'surcharge', label: 'WD surcharge', color: '#9e9e9e' },
]
const RISK_PARTS = [
  { key: 'rto', label: 'RTO risk', color: '#ed6c02' },
  { key: 'reattempt', label: 'reattempt', color: '#0288d1' },
  { key: 'lost', label: 'lost/dmg', color: '#d32f2f' },
]

// Cost waterfall, grouped into real money vs gated risk. Risk segments are drawn
// at their *applied* value (raw × risk_weight). Backward-compatible with rows
// stored before risk_weight: missing risk_weight ⇒ treat as ×1 (old full-cost).
// Weight basis for the quote (task-07). When proship priced at the WD-adjusted
// (expected-actual) weight, show declared → adjusted + the added grams and source, so
// the quote is reconcilable against the rate card (weight → slab → price). The WD
// surcharge is then 0 (folded into the quote).
function WdWeight({ c }) {
  const declared = c.declared_weight_g
  const adjusted = c.wd_adjusted_weight_g
  if (c.wd_adjusted && adjusted != null) {
    const added = c.wd_added_g != null ? c.wd_added_g : (declared != null ? adjusted - declared : null)
    return (
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.25 }}>
        Chargeable weight <b>{Math.round(adjusted)} g</b>
        {declared != null && ` (declared ${Math.round(declared)} g + WD ${Math.round(added)} g · ${c.wd_source || 'ledger'})`}
        {' '}— WD surcharge folded into the quote
      </Typography>
    )
  }
  if (declared != null) {
    return (
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.25 }}>
        Chargeable weight <b>{Math.round(declared)} g</b> (declared — no WD history)
      </Typography>
    )
  }
  return null
}

function CostWaterfall({ cb, wdAdjusted }) {
  const rw = cb.risk_weight != null ? cb.risk_weight : 1
  const rawRisk = RISK_PARTS.reduce((s, p) => s + (cb[p.key] || 0), 0)
  const riskApplied = cb.risk_cost != null ? cb.risk_cost : rawRisk
  const total = (cb.total != null ? cb.total : (cb.quote || 0) + (cb.surcharge || 0) + riskApplied) || 1
  const surcharge = cb.surcharge || 0
  const seg = (val, color, title) => (val > 0
    ? <Tooltip key={title} title={title}><Box sx={{ width: `${(val / total) * 100}%`, bgcolor: color }} /></Tooltip>
    : null)
  // The surcharge is ALWAYS the WD proxy (surcharge_weight × 30d avg extra × 30d rate) — a
  // courier-level statistical estimate, never this shipment's measured discrepancy. When the
  // weight was WD-adjusted (exact combo found in the ledger) it's zeroed — the discrepancy is
  // already in the chargeable weight, so a surcharge on top would double-count.
  const surchargeNote = wdAdjusted
    ? 'WD surcharge ₹0 — the weight discrepancy is priced into the chargeable weight above (no double-count).'
    : (surcharge > 0
        ? "WD surcharge = this courier's 30-day WD proxy (surcharge_weight × avg extra × rate) — a statistical estimate, not this combo's actual WD."
        : 'WD surcharge ₹0 — no WD history for this courier.')
  const surchargeTip = wdAdjusted
    ? 'WD-adjusted weight used — discrepancy folded into the rate-card quote, surcharge zeroed'
    : 'Courier-level weight-discrepancy proxy: surcharge_weight × avg extra charged (30d) × WD rate (30d)'
  return (
    <Box sx={{ mt: 0.75 }}>
      <Box sx={{ display: 'flex', height: 10, borderRadius: 1, overflow: 'hidden', gap: '1px' }}>
        {REAL_PARTS.map((p) => seg(cb[p.key] || 0, p.color, `${p.label}: ₹${(cb[p.key] || 0).toFixed(2)}`))}
        {RISK_PARTS.map((p) => seg((cb[p.key] || 0) * rw, p.color,
          `${p.label}: ₹${((cb[p.key] || 0) * rw).toFixed(2)} (raw ₹${(cb[p.key] || 0).toFixed(2)} × ${rw})`))}
      </Box>
      {/* Explicit 3-way split: rate-card + WD surcharge + risk = total. */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mt: 0.5 }}>
        <CostPart label="Rate-card" v={cb.quote || 0} color="#1976d2" />
        <Typography variant="caption" color="text.secondary">+</Typography>
        <Tooltip title={surchargeTip}>
          <span><CostPart label="WD surcharge" v={surcharge} color="#9e9e9e" /></span>
        </Tooltip>
        <Typography variant="caption" color="text.secondary">+</Typography>
        <CostPart label="Risk" v={riskApplied} color="#ed6c02" />
        <Typography variant="caption" sx={{ fontWeight: 700 }}>= ₹{Number(total).toFixed(2)}</Typography>
      </Box>
      <Typography variant="caption" color="text.secondary" component="div">{surchargeNote}</Typography>
      <Typography variant="caption" color="text.secondary" component="div">
        {(cb.quote || 0) === 0 && 'Rate-card ₹0 — no quote captured for this shadow row. '}
        {rw === 0
          ? `Risk off (risk_weight 0) — enabling ×1 would add ₹${rawRisk.toFixed(0)} (RTO + reattempt + lost).`
          : `Risk = risk_weight ×${rw}${rw !== 1 ? ` of ₹${rawRisk.toFixed(0)} raw` : ''} (RTO + reattempt + lost).`}
      </Typography>
    </Box>
  )
}

// One labelled component of the cost split, with a colour dot matching the bar.
function CostPart({ label, v, color }) {
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: color }} />
      <Typography variant="caption" color="text.secondary">{label} ₹{Number(v).toFixed(2)}</Typography>
    </Stack>
  )
}

// Speed↔cost split (older α-scale rows): how much of the blended score came from the
// speed term vs the cost term (magnitudes).
function SpeedCostSplit({ bt }) {
  const fast = Math.abs(bt.fast_weighted || 0)
  const cheap = Math.abs(bt.cheap_weighted || 0)
  const sum = fast + cheap || 1
  return (
    <Box sx={{ mt: 0.75 }}>
      <Box sx={{ display: 'flex', height: 10, borderRadius: 1, overflow: 'hidden' }}>
        <Tooltip title={`speed term ${bt.fast_weighted}`}><Box sx={{ width: `${(fast / sum) * 100}%`, bgcolor: '#2e7d32' }} /></Tooltip>
        <Tooltip title={`cost term ${bt.cheap_weighted}`}><Box sx={{ width: `${(cheap / sum) * 100}%`, bgcolor: '#1976d2' }} /></Tooltip>
      </Box>
      <Typography variant="caption" color="text.secondary">
        blend: speed {((fast / sum) * 100).toFixed(0)}% · cost {((cheap / sum) * 100).toFixed(0)}%
      </Typography>
    </Box>
  )
}
